import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Star, Clock, Check } from "lucide-react";
import MediaUpload from "@/components/MediaUpload";

const emptyT = { name: "", location: "", procedure: "", clinic_name: "", quote: "", rating: 5, avatar: "", photo: "", video_url: "", published: true };
const emptyB = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "My Global Dentist Team", tags: [], published: true };

export default function ContentAdmin() {
  const [testimonials, setTestimonials] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [posts, setPosts] = useState([]);
  const [tDialog, setTDialog] = useState(false);
  const [bDialog, setBDialog] = useState(false);
  const [editT, setEditT] = useState(null);
  const [editB, setEditB] = useState(null);
  const [tForm, setTForm] = useState(emptyT);
  const [bForm, setBForm] = useState(emptyB);

  const load = () => {
    api.get("/admin/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/blog", { params: { published: false } }).then((r) => setPosts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {}); }, []);

  // Testimonials
  const saveT = async () => {
    if (!tForm.name || !tForm.quote) { toast.error("Name and quote required"); return; }
    const payload = { ...tForm, rating: parseInt(tForm.rating) || 5 };
    try {
      if (editT) await api.put(`/admin/testimonials/${editT.id}`, { ...payload, id: editT.id });
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setTDialog(false); load();
    } catch { toast.error("Failed to save"); }
  };
  const togglePublish = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { ...t, published: !t.published });
      toast.success(t.published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };
  const removeT = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };

  // Blog
  const saveB = async () => {
    if (!bForm.title) { toast.error("Title required"); return; }
    const slug = bForm.slug || bForm.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const payload = { ...bForm, slug };
    try {
      if (editB) await api.put(`/admin/blog/${editB.id}`, { ...payload, id: editB.id });
      else await api.post("/admin/blog", payload);
      toast.success("Saved"); setBDialog(false); load();
    } catch { toast.error("Failed to save"); }
  };
  const removeB = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold">Content</h1>
      <p className="text-muted-foreground mt-1">Manage testimonials and blog guides.</p>

      <Tabs defaultValue="testimonials" className="mt-6">
        <TabsList data-testid="content-tabs">
          <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
        </TabsList>

        <TabsContent value="testimonials" className="mt-5">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">
              {testimonials.filter((t) => !t.published).length > 0
                ? <span className="text-primary font-semibold" data-testid="pending-count">{testimonials.filter((t) => !t.published).length} pending {testimonials.filter((t) => !t.published).length === 1 ? "story" : "stories"}</span>
                : "All caught up — no pending stories."}
            </p>
            <Button className="rounded-full" onClick={() => { setEditT(null); setTForm(emptyT); setTDialog(true); }} data-testid="add-testimonial-btn"><Plus className="w-4 h-4 mr-2" /> Add story</Button>
          </div>

          {testimonials.filter((t) => !t.published).length > 0 && (
            <div className="mb-8">
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Pending approval</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.filter((t) => !t.published).map((t) => (
                  <Card key={t.id} className="p-5 rounded-2xl border-primary/30 bg-primary/5" data-testid={`admin-testimonial-${t.id}`}>
                    <span className="inline-block text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-semibold mb-2">Pending</span>
                    {(t.photo || t.video_url) && (t.video_url ? <video src={t.video_url} className="w-full h-28 object-cover rounded-lg mb-2 bg-black" /> : <img src={t.photo} alt="" className="w-full h-28 object-cover rounded-lg mb-2" />)}
                    <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                    <p className="text-sm line-clamp-3">"{t.quote}"</p>
                    <p className="text-sm font-medium mt-3">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{[t.location, t.procedure, t.clinic_name].filter(Boolean).join(" · ")}</p>
                    {t.email && <p className="text-xs text-primary mt-1" data-testid={`pending-email-${t.id}`}>✉ {t.email}</p>}
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="rounded-full flex-1" onClick={() => togglePublish(t)} data-testid={`approve-testimonial-${t.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setEditT(t); setTForm({ ...emptyT, ...t }); setTDialog(true); }} data-testid={`edit-testimonial-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => removeT(t.id)} data-testid={`delete-testimonial-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <h3 className="font-heading font-bold text-lg mb-3">Published</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.filter((t) => t.published).map((t) => (
              <Card key={t.id} className="p-5 rounded-2xl" data-testid={`admin-testimonial-${t.id}`}>
                {(t.photo || t.video_url) && (t.video_url ? <video src={t.video_url} className="w-full h-28 object-cover rounded-lg mb-2 bg-black" /> : <img src={t.photo} alt="" className="w-full h-28 object-cover rounded-lg mb-2" />)}
                <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-sm line-clamp-3">"{t.quote}"</p>
                <p className="text-sm font-medium mt-3">{t.name}</p>
                <p className="text-xs text-muted-foreground">{[t.location, t.procedure, t.clinic_name].filter(Boolean).join(" · ")}</p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" className="rounded-full flex-1" onClick={() => togglePublish(t)} data-testid={`unpublish-testimonial-${t.id}`}>Unpublish</Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setEditT(t); setTForm({ ...emptyT, ...t }); setTDialog(true); }} data-testid={`edit-testimonial-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => removeT(t.id)} data-testid={`delete-testimonial-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </Card>
            ))}
            {testimonials.filter((t) => t.published).length === 0 && <p className="text-muted-foreground text-sm">No published stories yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="blog" className="mt-5">
          <div className="flex justify-end mb-4"><Button className="rounded-full" onClick={() => { setEditB(null); setBForm(emptyB); setBDialog(true); }} data-testid="add-post-btn"><Plus className="w-4 h-4 mr-2" /> Add post</Button></div>
          <Card className="rounded-2xl overflow-hidden">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0" data-testid={`admin-post-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground">{p.published ? "Published" : "Draft"} · /{p.slug}</p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setEditB(p); setBForm({ ...emptyB, ...p }); setBDialog(true); }} data-testid={`edit-post-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => removeB(p.id)} data-testid={`delete-post-${p.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="text-center text-muted-foreground py-12">No posts yet.</p>}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Testimonial dialog */}
      <Dialog open={tDialog} onOpenChange={setTDialog}>
        <DialogContent className="max-w-lg" data-testid="testimonial-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editT ? "Edit" : "Add"} testimonial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Name *</Label><Input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="testimonial-name-input" /></div>
              <div className="space-y-2"><Label>Location</Label><Input value={tForm.location} onChange={(e) => setTForm({ ...tForm, location: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Procedure</Label><Input value={tForm.procedure} onChange={(e) => setTForm({ ...tForm, procedure: e.target.value })} /></div>
              <div className="space-y-2"><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={tForm.rating} onChange={(e) => setTForm({ ...tForm, rating: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Clinic name</Label>
              <Select value={tForm.clinic_name} onValueChange={(v) => setTForm({ ...tForm, clinic_name: v })}>
                <SelectTrigger data-testid="testimonial-clinic-select"><SelectValue placeholder="Select a clinic" /></SelectTrigger>
                <SelectContent>{clinics.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Quote *</Label><Textarea rows={3} value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} data-testid="testimonial-quote-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <MediaUpload label="Photo" accept="image/*" kind="image" value={tForm.photo} onChange={(v) => setTForm({ ...tForm, photo: v })} testid="testimonial-photo-upload" />
              <MediaUpload label="Video" accept="video/*" kind="video" value={tForm.video_url} onChange={(v) => setTForm({ ...tForm, video_url: v })} testid="testimonial-video-upload" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><Label>Published</Label><p className="text-xs text-muted-foreground">Visible on the public stories page</p></div>
              <Switch checked={tForm.published} onCheckedChange={(v) => setTForm({ ...tForm, published: v })} data-testid="testimonial-published-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTDialog(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={saveT} data-testid="save-testimonial-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blog dialog */}
      <Dialog open={bDialog} onOpenChange={setBDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editB ? "Edit" : "Add"} post</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} data-testid="post-title-input" /></div>
            <div className="space-y-2"><Label>Excerpt</Label><Textarea rows={2} value={bForm.excerpt} onChange={(e) => setBForm({ ...bForm, excerpt: e.target.value })} /></div>
            <div className="space-y-2"><Label>Cover image URL</Label><Input value={bForm.cover_image} onChange={(e) => setBForm({ ...bForm, cover_image: e.target.value })} placeholder="https://…" /></div>
            <div className="space-y-2"><Label>Content <span className="text-xs text-muted-foreground">(use "## " for headings, "- " for bullets)</span></Label><Textarea rows={8} value={bForm.content} onChange={(e) => setBForm({ ...bForm, content: e.target.value })} data-testid="post-content-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tags (comma separated)</Label><Input value={(bForm.tags || []).join(", ")} onChange={(e) => setBForm({ ...bForm, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></div>
              <div className="space-y-2"><Label>Author</Label><Input value={bForm.author} onChange={(e) => setBForm({ ...bForm, author: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><Label>Published</Label><p className="text-xs text-muted-foreground">Visible on public site</p></div>
              <Switch checked={bForm.published} onCheckedChange={(v) => setBForm({ ...bForm, published: v })} data-testid="post-published-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setBDialog(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={saveB} data-testid="save-post-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
